import { Component, OnInit, ViewChild } from '@angular/core';
import { AppInformationService, AuthService, GenesisUnitOfWork, InvoiceService, UtilityService } from 'app/core/core.module';
import { Dealer, FeeType, InvoicePaymentMethod, OrderLineDetail, PackingSlip, Payment, PaymentCredit, PaymentLine, PaymentLineType } from 'app/core/model/entity-model';
import { Utility } from 'app/core/utility';
import { InputDialogComponent } from 'app/shared/input-dialog/input-dialog.component';
import { SelectDialogComponent } from 'app/shared/select-dialog/select-dialog.component';
import { SharedModule } from 'app/shared/shared.module';
import { DxDataGridComponent } from 'devextreme-angular';
import { alert, confirm } from 'devextreme/ui/dialog';
import { default as validationEngine } from 'devextreme/ui/validation_engine';
import _ from 'lodash';
import moment from 'moment';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { Subject } from 'rxjs';
@Component({
    selector: 'app-payment-dialog',
    templateUrl: './payment-dialog.component.html',
    styleUrls: ['./payment-dialog.component.scss'],
    standalone: false
})
export class PaymentDialogComponent implements OnInit {
    result: Subject<any> = new Subject<any>();
    mode: string = 'salesOrder';
    payment: Payment;
    paymentLines: PaymentLine[];
    dealer: Dealer;
    invoices: PackingSlip[];
    orderLineDetails: OrderLineDetail[];
    invoiceBalance: number;
    paymentMethods: any;
    creditCards: any;
    invoiceLines: any;
    paymentModes = ['Pay Full Amount', 'Pay Custom Amount', 'Apply Percentage'];
    selectedMode: any;
    percentage: number = 10;
    totalBalance: number = 0;
    totalFreight: number = 0;
    totalFreightPaid: number = 0;
    totalTaxPaid: number = 0;
    validationGroup = 'paymentValidationGroup';
    errorAction = 'saving';
    isNewPayment: boolean = false;
    dealerCredits: PaymentCredit[];
    partialCredits: any[] = [];
    minId: number;
    overpaymentCreditPayment: Payment;
    canAddNorthPointFee: boolean = false;
    canDelete: boolean = false;
    autoTotalSet: boolean = false;
    feeTypes: FeeType[];
    selectedAmount: number = 0;
    overPayments: any[];
    availableOverPaymentAmount = 0;

    @ViewChild('linesGrid') linesGrid: DxDataGridComponent;
    @ViewChild('creditsGrid') creditsGrid: DxDataGridComponent;
    onExporting = SharedModule.onExporting;

    constructor(
        public modalRef: BsModalRef,
        private uow: GenesisUnitOfWork,
        private appInfo: AppInformationService,
        private auth: AuthService,
        private modalService: BsModalService,
        private utility: UtilityService,
        private invoiceService: InvoiceService
    ) {}

    async ngOnInit() {
        this.creditCards = await this.uow.creditCards.whereActive('displayOrder');
        this.paymentMethods = await this.uow.invoicePaymentMethods.whereActive('displayOrder');
        this.feeTypes = (await this.uow.feeTypes.whereActive('displayOrder')) as FeeType[];
        this.canAddNorthPointFee = this.selectedMode === '' && this.payment.paymentMethodId === InvoicePaymentMethod.Methods.NorthPoint;
        if (this.payment) {
            await this.getDataLinesForPaymentId(this.payment.id);
        } else {
            this.isNewPayment = true;
            this.selectedMode = 'Pay Full Amount';
            await this.getDataLines();
            this.updateLineTotals();
            this.calcTotal();
            this.payment = this.uow.payments.createEntity({
                dealerId: this.dealer.id,
                paymentDate: new Date(),
                total: this.totalBalance,
                created: new Date(),
                createdBy: this.auth.currentUser.userName,
            }) as Payment;
            this.setMinLine();
        }
        await this.getDealerCredits();
        this.setPermissions();
    }

    setPermissions() {
        this.canDelete = (this.auth.hasRole('Payments:CanDeleteAfterExport') || !this.payment.quickBooksId) && !this.isNewPayment;
    }

    async getDataLinesForPaymentId(paymentId: number) {
        this.paymentLines = (await this.uow.getPaymentLinesByPaymentIds([paymentId], 'orderLineDetail.orderLine.order, packingSlip, invoiceFee.feeType')) as PaymentLine[];
        const invoiceLines = [];
        this.paymentLines.forEach((line) => {
            if (line.paymentLineTypeId === PaymentLineType.Types.Freight) {
                invoiceLines.push({
                    id: line.id,
                    orderLine: { partNumber: `Freight for Invoice ${line.packingSlip.invoiceNumber}` },
                    packingSlip: line.packingSlip,
                    parentAmount: line.packingSlip.freight,
                    paymentTotal: line.amount,
                });
            } else if (line.paymentLineTypeId === PaymentLineType.Types.Tax) {
                invoiceLines.push({
                    id: line.id,
                    orderLine: { partNumber: `Tax for Invoice ${line.packingSlip.invoiceNumber}` },
                    packingSlip: line.packingSlip,
                    parentAmount: line.packingSlip.tax,
                    paymentTotal: line.amount,
                });
            } else if (
                line.paymentLineTypeId === PaymentLineType.Types.NorthPointFee ||
                line.paymentLineTypeId === PaymentLineType.Types.Credit ||
                line.paymentLineTypeId === PaymentLineType.Types.OverPayment
            ) {
                invoiceLines.push({
                    id: line.id,
                    orderLine: { partNumber: line.paymentLineType.name },
                    packingSlip: line.packingSlip,
                    parentAmount: line.amount,
                    paymentTotal: 0,
                });
            } else if (line.paymentLineTypeId === PaymentLineType.Types.Fee) {
                if (line.invoiceFee) {
                    invoiceLines.push({
                        id: line.id,
                        orderLine: { partNumber: `${line.invoiceFee.feeType.name} Fee for ${line.invoiceFee.invoiceId ? 'Invoice ' + line.packingSlip.invoiceNumber : 'Sales Order'}` },
                        packingSlip: line.packingSlip,
                        parentAmount: line.invoiceFee.amount,
                        paymentTotal: line.amount,
                        paymentLineTypeId: line.paymentLineTypeId,
                    });
                } else {
                    invoiceLines.push({
                        id: line.id,
                        orderLine: { partNumber: `Fee` },
                        packingSlip: line.packingSlip,
                        parentAmount: line.amount,
                        paymentTotal: line.amount,
                        paymentLineTypeId: line.paymentLineTypeId,
                    });
                }
            } else {
                invoiceLines.push({
                    id: line.id,
                    orderLine: line?.orderLineDetail?.orderLine,
                    packingSlip: line.packingSlip,
                    quantity: line.orderLineDetail?.quantity,
                    displayOrder: line.orderLineDetail?.displayOrder,
                    vIN: line.orderLineDetail?.vIN,
                    parentAmount: line.orderLineDetail?.quantity * line.orderLineDetail?.price,
                    paymentTotal: line.amount,
                });
            }
        });
        this.invoiceLines = invoiceLines;
    }

    async getDataLines() {
        let lines = [];
        let orderLineDetails = [];
        let invoiceFees = [];
        this.totalBalance = 0;

        if (this.mode === 'invoices') {
            if (this.invoices && this.invoices.length > 0) {
                //get the packSlip.orderLineDetails rather than expecting them to come in on the object
                for (let index = 0; index < this.invoices.length; index++) {
                    const invoice = this.invoices[index];
                    const invoiceDetails = (await this.uow.getOrderLineDetailsByPackSlipId(invoice.id, 'orderLine.order, packingSlip.invoiceFees')) as OrderLineDetail[];

                    const lines = _.orderBy(invoiceDetails, '_displayOrder');
                    lines.forEach((line) => {
                        if (!line.deleted) orderLineDetails.push(line);
                    });

                    //check for InvoiceFees
                    if (!!invoice.invoiceFees && invoice.invoiceFees.length > 0) {
                        invoice.invoiceFees.forEach((line) => {
                            if (!line.deleted) invoiceFees.push(line);
                        });
                    }
                }
            }
        } else {
            orderLineDetails = this.orderLineDetails.filter((x) => !x.deleted);
        }

        orderLineDetails.forEach((line: any) => {
            line.packingSlipId = line.packingSlipId;
            line.parentAmount = line.quantity * line.price;
            line.parentBalance = line.balance;
            this.totalBalance += line.balance;
            line.paymentLineTypeId = PaymentLineType.Types.OrderLineDetail;
            lines.push(line);
        });
        invoiceFees.forEach((line: any) => {
            line.packingSlipId = line.packingSlipId;
            line.orderLine = { partNumber: `Fee for Invoice ${line.invoice.invoiceNumber}` };
            line.parentAmount = line.amount;
            line.parentBalance = line.amount;
            this.totalBalance += line.amount;
            line.paymentLineTypeId = PaymentLineType.Types.Fee;
            lines.push(line);
        });
        this.invoiceLines = lines;
        await this.getPayments(orderLineDetails);
    }

    async getDealerCredits() {
        const credits = await this.uow.getPaymentCreditsByDealerId(this.dealer.id);
        this.dealerCredits = credits;
    }

    updateLineTotals() {
        this.invoiceLines.forEach((line: any) => {
            if (line.paymentLineTypeId !== 3) {
                if (this.selectedMode === 'Pay Full Amount') line.amount = line.parentBalance;
                // if (this.selectedMode === 'Pay Custom Amount') line.amount = 0;
                else if (this.selectedMode === 'Apply Percentage') line.amount = line.parentBalance * (this.percentage / 100);
            }
        });
    }

    async getPayments(orderLineDetails: OrderLineDetail[]) {
        const paymentList = [];

        // const orderLineDetails = _.orderBy(invoice.orderLineDetails, '_displayOrder');
        orderLineDetails.forEach((detail) => {
            if (!!detail.paymentLines && detail.paymentLines.length > 0) {
                detail.paymentLines
                    .filter((line) => line.deleted === null)
                    .forEach((paymentLine) => {
                        if (!_.includes(paymentList, paymentLine.paymentId)) paymentList.push(paymentLine.paymentId);
                    });
            }
        });

        if (this.mode === 'invoices') {
            await this.getTotalFreightPaid(paymentList);
            await this.getTotalTaxPaid(paymentList);
        }
        this.overPayments = await this.uow.getDealerOverPayments(this.dealer.id);
        this.availableOverPaymentAmount = _.sumBy(this.overPayments, 'availableAmount');
    }

    async getTotalFreightPaid(paymentIds: number[]) {
        const paymentLines = await this.uow.getPaymentLinesByPaymentIds(paymentIds);
        this.totalFreightPaid = 0;
        this.setMinLine();
        this.invoices.forEach((invoice) => {
            const freightPaid =
                _.sum(
                    paymentLines.map((x) => {
                        if (x.paymentLineTypeId == PaymentLineType.Types.Freight && x.packingSlipId == invoice.id) {
                            return x.amount;
                        }
                    })
                ) ?? 0;
            this.totalFreightPaid += freightPaid;
            if (freightPaid < invoice.freight) {
                this.invoiceLines.push({
                    id: this.minId,
                    packingSlip: { packingSlipNumber: invoice.packingSlipNumber, invoiceNumber: invoice.invoiceNumber },
                    packingSlipId: invoice.id,
                    orderLine: { partNumber: `Freight for Invoice ${invoice.invoiceNumber}` },
                    parentAmount: invoice.freight,
                    paymentTotal: freightPaid,
                    parentBalance: invoice.freight - freightPaid,
                    balance: invoice.freight - freightPaid,
                    amount: this.selectedMode === 'Pay Custom Amount' ? 0 : invoice.freight - freightPaid,
                    paymentLineTypeId: PaymentLineType.Types.Freight,
                    invoice: invoice,
                });
                this.minId--;
            }
        });
        this.totalFreight =
            _.sum(
                this.invoices.map((x) => {
                    return x.freight;
                })
            ) - this.totalFreightPaid;
    }

    async getTotalTaxPaid(paymentIds: number[]) {
        const paymentLines = await this.uow.getPaymentLinesByPaymentIds(paymentIds);
        let i = -1;
        this.totalTaxPaid = 0;
        this.invoices.forEach((invoice) => {
            const taxPaid =
                _.sum(
                    paymentLines.map((x) => {
                        if (x.paymentLineTypeId == PaymentLineType.Types.Tax && x.packingSlipId == invoice.id) {
                            return x.amount;
                        }
                    })
                ) ?? 0;
            this.totalTaxPaid += taxPaid;
            if (taxPaid < invoice.tax) {
                this.invoiceLines.push({
                    id: this.minId,
                    packingSlip: { packingSlipNumber: invoice.packingSlipNumber, invoiceNumber: invoice.invoiceNumber },
                    packingSlipId: invoice.id,
                    orderLine: { partNumber: `Tax for Invoice ${invoice.invoiceNumber}` },
                    parentAmount: invoice.tax,
                    paymentTotal: taxPaid,
                    parentBalance: invoice.tax - taxPaid,
                    balance: invoice.tax - taxPaid,
                    amount: this.selectedMode === 'Pay Custom Amount' ? 0 : invoice.tax - taxPaid,
                    paymentLineTypeId: PaymentLineType.Types.Tax,
                    invoice: invoice,
                });
                this.minId--;
            }
        });
        this.totalFreight =
            _.sum(
                this.invoices.map((x) => {
                    return x.tax;
                })
            ) - this.totalTaxPaid;
    }

    onToolbarPreparing(e) {
        e.toolbarOptions.items.unshift(
            {
                location: 'before',
                template: 'toolbarButtons',
            },
            {
                location: 'before',
                template: 'toolbarTextStart',
            },
            {
                location: 'before',
                template: 'toolbarButtonsStart',
            },
            {
                location: 'after',
                template: 'toolbarButtonsEnd',
            },
            {
                location: 'after',
                template: 'toolbarText',
            },

            {
                widget: 'dxButton',
                options: {
                    icon: 'revert',
                    elementAttr: { title: 'Reset Filters' },
                    onClick: () => {
                        e.component.clearFilter();
                    },
                },
                location: 'after',
            }
        );
    }

    selectionChanged(e) {
        this.selectedAmount = 0;
        const selectedRows = this.linesGrid.instance.getSelectedRowsData();
        selectedRows.forEach((row) => {
            this.selectedAmount += row.amount;
        });
    }

    onModeUpdate(e) {
        this.setPaymentTotal();
        this.updateLineTotals();
        this.calcTotal();
        this.onMethodUpdate();
    }

    onMethodUpdate(e?) {
        this.canAddNorthPointFee = this.payment.paymentMethodId === InvoicePaymentMethod.Methods.NorthPoint;
        if (this.payment.paymentMethodId === InvoicePaymentMethod.Methods.Cash || this.payment.paymentMethodId === InvoicePaymentMethod.Methods.CreditCard) {
            this.payment.referenceNumber = moment(new Date()).format('YYYYMMDD');
        }
        if (this.payment.paymentMethodId === InvoicePaymentMethod.Methods.ACH) {
            this.achCheck();
        }
        if (this.payment.paymentMethodId !== InvoicePaymentMethod.Methods.CreditCard) {
            this.payment.creditCardId = null;
            this.payment.creditCardLastDigits = null;
        }
    }

    async achCheck() {
        this.appInfo.pleaseWaitShow();
        const detailIds = this.invoiceLines.map((x) => {
            if (x.id > 0) return x.id;
        });
        const result = await this.utility.paymentACHCheck(detailIds);
        this.appInfo.pleaseWaitHide();
        if (result) {
            await alert(`Some of the details on this payment have not had ACH Pricing Applied`, `${this.appInfo.settings.company}`);
        }
    }

    onPercentageChanged(e) {
        this.updateLineTotals();
        this.linesGrid.instance.refresh();
    }
    setPaymentTotal() {
        if (this.selectedMode === 'Pay Full Amount') this.payment.total = this.totalBalance;
        if (this.selectedMode !== 'Pay Full Amount' && !this.autoTotalSet) this.payment.total = 0;
        // this.autoTotalSet = false;
    }
    calcTotal() {
        const balance = _.sum(
            this.invoiceLines.map((x) => {
                if (x.paymentLineTypeId !== PaymentLineType.Types.Freight) return x.balance;
            })
        );
        if (this.mode === 'invoices') {
            this.totalFreight =
                _.sum(
                    this.invoices.map((x) => {
                        return x.freight;
                    })
                ) - this.totalFreightPaid;
        }
        this.totalBalance = balance + this.totalFreight;
    }
    async autoApplyPartialPayment() {
        if (!this.payment || this.payment.total === 0) {
            await alert(`No Payment Total`, `${this.appInfo.settings.company}`);
            return;
        }
        this.invoiceLines.forEach((line) => {
            line.amount = 0;
        });
        let partialTotal = this.payment.total;
        _.orderBy(this.invoiceLines, '_displayOrder').forEach((line) => {
            // console.log('line', line)
            // console.log('partialTotal', partialTotal)
            if (partialTotal > 0 && line.balance > 0) {
                if (line.balance < partialTotal) line.amount = line.balance;
                else line.amount = partialTotal;
                partialTotal -= line.balance;
            }
        });
        this.linesGrid.instance.refresh();
    }

    rowPrepare(e) {
        if (e.rowType === 'data') {
            if (e.data.amount > e.data.parentBalance) {
                e.cells.forEach((cell) => {
                    if (cell.column.dataField === 'amount') {
                        if (cell.cellElement) {
                            cell.cellElement.className = cell.cellElement.className + ' overpayment';
                        }
                    }
                });
            }
            e.cells.forEach((cell) => {
                if (cell.column.dataField === 'amount') {
                    if (cell.cellElement) {
                        cell.cellElement.className = cell.cellElement.className + ' amount-cell';
                        if (cell.value === 0) {
                            cell.cellElement.className = cell.cellElement.className + ' zero-amount';
                        }
                        if (cell.value < 0) {
                            cell.cellElement.className = cell.cellElement.className + ' overpayment';
                        }
                    }
                }
                if (cell.column.dataField === 'parentBalance') {
                    if (cell.cellElement && cell.value === 0) cell.cellElement.className = cell.cellElement.className + ' zero-balance';
                }
            });
        }
    }

    rowDoubleClick(e) {
        if (!e.data) return;

        if (this.selectedMode === 'Pay Custom Amount') {
            if (e.data.amount === 0) e.data.amount = e.data.parentBalance;
            else e.data.amount = 0;
            this.linesGrid.instance.refresh();
            this.calcTotal();
        }
        this.selectionChanged(null);
    }

    async save(readyToExport?: boolean) {
        this.appInfo.pleaseWaitShow();
        const success = await Promise.resolve(this.validate());
        if (!success) {
            this.appInfo.pleaseWaitHide();
            return;
        }

        if (this.payment.paymentMethodId === InvoicePaymentMethod.Methods.CreditCard) {
            this.appInfo.pleaseWaitHide();

            const result = await confirm(`Payments by Credit Card will not export to QB. Continue?`, `${this.appInfo.settings.company}`);
            if (!result) return;

            this.appInfo.pleaseWaitShow();
            this.payment.readyToExport = new Date();
            this.payment.quickBooksId = 'Marked paid w/o QB - Credit Card';
            this.payment.quickBooksLastExport = new Date();
        }

        await this.overPaymentCheck(readyToExport);
        await this.overPaymentCreditCheck();

        if (readyToExport) this.payment.readyToExport = new Date();
        await this.uow.commit([this.payment]);

        if (this.overpaymentCreditPayment) this.overpaymentCreditPayment.notes = `OverPayment from PaymentId: ${this.payment.id}`;
        await this.uow.commit();
        this.result.next({ success: true, value: this.payment, orderLineDetails: this.invoiceLines, paymentMode: this.selectedMode, overPayment: this.overpaymentCreditPayment });
    }

    validate() {
        const groupConfig = validationEngine.getGroupConfig(this.validationGroup);
        if (groupConfig) {
            const result = validationEngine.validateGroup(this.validationGroup);
            if (!result.isValid) {
                let message = '';
                result.brokenRules.forEach((brokenRule) => {
                    if (brokenRule.type === 'custom') message = `${brokenRule.message}\n${message}`;
                });
                this.appInfo.notifyToast(`Please fix errors before ${this.errorAction}.\n\n${message}`, 'error');
                return false;
            }
        }
        return true;
    }

    validatePaymentTotal = (e) => {
        // rounding to prevent floating point errors
        const lineTotal = Utility.round(
            _(this.invoiceLines).sumBy((l) => l.amount),
            2
        );
        e.value = Utility.round(e.value, 2);
        return lineTotal === e.value;
    };

    close() {
        this.uow.rollback();
        this.result.next({ success: false });
    }

    async delete() {
        if (!this.canDelete) return;
        const message = `Are you sure you want to DELETE this payment?${
            this.payment.quickBooksId ? '<br>This Payment has already been exported to QuickBooks, you will need to manually delete it from QuickBooks.  Continue?' : ''
        }`;

        const success = await confirm(`${message}`, this.appInfo.settings.company);
        if (!success) return;

        this.appInfo.pleaseWaitShow();
        const today = new Date();
        this.payment.deleted = today;
        this.payment.deletedBy = this.auth.currentUser.userName;
        this.paymentLines.forEach((line) => {
            line.deleted = today;
            line.deletedBy = this.auth.currentUser.userName;

            if (line.packingSlip) line.packingSlip.paid = null;
        });

        const invoiceIds = this.invoiceLines.filter(x => x.packingSlip !== null).map((x) => {
            return x.packingSlip.id;
        });
        const invoices = (await this.uow.packingSlips.byIds(invoiceIds)) as PackingSlip[];
        invoices.forEach((invoice) => {
            invoice.paid = null;
        });
        await this.uow.commit();
        this.appInfo.pleaseWaitHide();

        this.result.next({ success: true });
    }

    async addNorthPointLines() {
        const invoiceCount = this.invoices.length;
        if (this.invoices.length > 1) {
            await alert(`There are ${this.invoices.length} Invoices, the fee will be split between them.`, `${this.appInfo.settings.company}`);
        }
        const feeAmount = (this.payment as any).northPointFee / this.invoices.length;
        const startingFee = (this.payment as any).northPointFee;
        let totalFee = 0;
        console.log(feeAmount);

        for (let index = 0; index < this.invoices.length; index++) {
            const invoice = this.invoices[index];
            const lastOne = index === this.invoices.length - 1;

            this.invoiceLines.push({
                id: Utility.setMinLine(this.invoiceLines),
                packingSlipId: invoice.id,
                orderLine: { partNumber: `NorthPoint Fee` },
                packingSlip: { packingSlipNumber: invoice.packingSlipNumber, invoiceNumber: invoice.invoiceNumber },
                // quantity: 1,
                parentAmount: 0,
                paymentTotal: 0,
                parentBalance: 0,
                amount: (lastOne ? Math.abs(totalFee - startingFee) : Utility.round(feeAmount, 2)) * -1,
                paymentLineTypeId: PaymentLineType.Types.NorthPointFee,
            });
            totalFee += Utility.round(feeAmount, 2);
            this.minId--;
        }
    }

    async addFeeLine() {
        const modalRef = this.modalService.show(InputDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                dialogHeader: 'Fees',
                dialogPrompt: 'Select Fee',
                dialogValue: 1,
                dialogValueArray: this.feeTypes,
                mode: 'selectWithId',
            },
        });

        const promise = new Promise<any>(async (resolve, reject) => {
            modalRef.content.result.subscribe(async (result) => {
                if (result.result && !!result.value) {
                    resolve(result.value);
                }
                modalRef.hide();
                resolve(null);
            });
        });

        const selectedFeeId = await Promise.resolve(promise);
        if (!selectedFeeId) return;

        const feeType = _.find(this.feeTypes, { id: selectedFeeId });

        if (this.mode === 'invoices') {
            this.invoices.forEach((invoice) => {
                const invoiceTotal = _.sumBy(
                    this.invoiceLines.filter((x) => x.packingSlipId === invoice.id),
                    'parentBalance'
                );
                const fee = feeType.percentageBased ? Utility.round(invoiceTotal * feeType.defaultAmount, 2) : feeType.defaultAmount;

                this.invoiceLines.push({
                    id: Utility.setMinLine(this.invoiceLines),
                    packingSlipId: invoice.id,
                    orderLine: { partNumber: `${feeType.name} Fee for Invoice ${invoice.invoiceNumber}` },
                    packingSlip: { packingSlipNumber: invoice.packingSlipNumber, invoiceNumber: invoice.invoiceNumber },
                    // quantity: 0,
                    parentAmount: fee,
                    paymentTotal: 0,
                    parentBalance: fee,
                    amount: fee,
                    paymentLineTypeId: PaymentLineType.Types.Fee,
                    feeTypeId: selectedFeeId,
                });
            });
        } else {
            const invoiceTotal = _.sumBy(this.invoiceLines, 'parentBalance');
            const fee = feeType.percentageBased ? Utility.round(invoiceTotal * feeType.defaultAmount, 2) : feeType.defaultAmount;

            //get a list of all the salesOrders
            const salesOrders = _.uniqBy(this.invoiceLines, 'orderLine.order.invoiceNumber');
            console.log('salesOrders', salesOrders);
            salesOrders.forEach((salesOrder: any) => {
                const salesOrderNumber = salesOrder.orderLine.order.invoiceNumber;
                this.invoiceLines.push({
                    id: Utility.setMinLine(this.invoiceLines),
                    packingSlipId: null,
                    salesOrderId: salesOrder.orderLine.orderId,
                    orderLine: { partNumber: `${feeType.name} Fee for Sales Order ${salesOrderNumber}`, order: { invoiceNumber: salesOrderNumber } },
                    packingSlip: {},
                    // quantity: 0,
                    parentAmount: fee,
                    paymentTotal: 0,
                    parentBalance: fee,
                    amount: fee,
                    paymentLineTypeId: PaymentLineType.Types.Fee,
                    feeTypeId: selectedFeeId,
                });
            });
        }
    }

    async remove() {
        const selectedRows = this.linesGrid.instance.getSelectedRowsData();
        if (!selectedRows) return;

        selectedRows.forEach((row) => {
            // console.log(row);
            const partialCredit = _.find(this.partialCredits, { lineId: row.id });
            if (!!partialCredit) {
                partialCredit.credit.entityAspect.setDeleted();
                // this.dealerCredits.splice(partialCredit.credit,1)

                partialCredit.selectedCredit.appliedToPaymentId = null;
                partialCredit.selectedCredit.applied = null;
            }
            if (row.paymentLineTypeId === PaymentLineType.Types.Fee) {
                row.deleted = new Date(); //deletes the invoice fee
            }
            this.invoiceLines.splice(_.findIndex(this.invoiceLines, { id: row.id }), 1);
        });

        this.calcTotal();
        this.setPaymentTotal();
    }

    async addCreditLine() {
        const selectedCredit = this.creditsGrid.instance.getSelectedRowsData()[0];
        if (!selectedCredit) return;

        const selectedRows = this.linesGrid.instance.getSelectedRowsData();
        const selectedRow = selectedRows[0];
        console.log(selectedRows);
        let minId = Utility.setMinLine(this.invoiceLines);

        if (selectedCredit.fromPaymentId) {
            if (selectedRows && selectedRows.length > 0 && selectedRows.length !== 1) {
                await alert(`Select one line at a time to apply credits.`, `${this.appInfo.settings.company}`);
                return;
            }
            if (!selectedRow || selectedRow.amount === 0) {
                await alert(`OverPayments need to be applied to a line with a balance greater than $0.00. Select a line to apply credit`, `${this.appInfo.settings.company}`);
                return;
            } else {
                //check if the selected line is more than the credit amount
                let creditAmount = 0;
                let creditBalance = 0;
                let createPartialCredit = false;

                if (selectedCredit.fromPaymentId) {
                    const result = await confirm(`Are you sure you want to apply the credit to '${selectedRow.orderLine.partNumber} `, `${this.appInfo.settings.company}'`);
                    if (!result) return;
                }
                creditAmount = selectedCredit.creditAmount;
                if (selectedRow.amount < creditAmount) {
                    creditBalance = creditAmount - selectedRow.amount;
                    creditAmount = selectedRow.amount;
                    const result = await confirm(
                        `The selected credit amount is greater than the total of the lines.  Do you still want to apply this credit?
                    Selecting Yes will create a credit for the remaining balance of $${creditBalance.toFixed(2)}.
                    `,
                        `${this.appInfo.settings.company}`
                    );
                    if (!result) return;
                    createPartialCredit = true;
                } else {
                    selectedRow.amount = creditAmount;
                    selectedRow.parentBalance -= creditAmount;
                    creditBalance = 0;
                    createPartialCredit = false;
                }

                this.invoiceLines.push({
                    id: minId,
                    packingSlipId: selectedRow.packingSlipId,
                    orderLine: { partNumber: `Credit Line applied to '${selectedRow.orderLine.partNumber}'` },
                    packingSlip: { packingSlipNumber: selectedRow.packingSlip.packingSlipNumber, invoiceNumber: selectedRow.packingSlip.invoiceNumber },
                    parentAmount: 0,
                    paymentTotal: 0,
                    parentBalance: creditAmount * -1,
                    amount: creditAmount * -1,
                    paymentLineTypeId: PaymentLineType.Types.Credit,
                    //fields specific to credits
                    creditId: selectedCredit.id,
                    creditBalance: creditBalance,
                });

                let credit: PaymentCredit;
                if (createPartialCredit) {
                    credit = this.uow.paymentCredits.createEntity({
                        dealerId: selectedCredit.dealerId,
                        paymentCreditTypeId: selectedCredit.paymentCreditTypeId,
                        fromPaymentId: selectedCredit.fromPaymentId,
                        creditAmount: creditBalance,
                        notes: `Created as partial credit from Id: ${selectedCredit.id}`,
                        created: new Date(),
                        createdBy: this.auth.currentUser.userName,
                    }) as PaymentCredit;
                    this.dealerCredits.push(credit);
                }

                this.partialCredits.push({
                    lineId: minId,
                    credit: createPartialCredit ? credit : null,
                    selectedCredit: selectedCredit,
                });

                //updated the selected Credit
                selectedCredit.appliedToPaymentId = this.payment.id;
                selectedCredit.applied = new Date();

                this.linesGrid.instance.refresh();
            }
        } else {
            //get the total of the selected credits
            const lineTotal = this.getLineTotal();
            let creditAmount = selectedCredit.creditAmount;
            let creditBalance = 0;
            let createPartialCredit = false;

            if (lineTotal < creditAmount) {
                creditBalance = creditAmount - lineTotal;
                const result = await confirm(
                    `The selected credit amount is greater than the total of the lines.  Do you still want to apply this credit?
            Selecting Yes will create a credit for the remaining balance of $${creditBalance.toFixed(2)}.
            `,
                    `${this.appInfo.settings.company}`
                );
                if (!result) return;
                creditAmount = lineTotal;
                createPartialCredit = true;
            }
            this.invoiceLines.forEach((line) => {
                if (line.parentBalance > 0) {
                    //is balance greater than credit
                }
            });

            let firstNonZero = _.find(this.invoiceLines, (x) => {
                return x.parentBalance > 0;
            });
            firstNonZero = this.invoiceLines[0];
            console.log(firstNonZero);

            this.invoiceLines.push({
                id: Utility.setMinLine(this.invoiceLines),
                packingSlipId: firstNonZero.packingSlipId,
                orderLine: { partNumber: `Credit Line applied to '${selectedRow.orderLine.partNumber}'` },
                packingSlip: { packingSlipNumber: firstNonZero.packingSlip.packingSlipNumber, invoiceNumber: firstNonZero.packingSlip.invoiceNumber },
                parentAmount: 0,
                paymentTotal: 0,
                parentBalance: creditAmount * -1,
                amount: creditAmount * -1,
                paymentLineTypeId: PaymentLineType.Types.Credit,
                //fields specific to credits
                creditId: selectedCredit.id,
                creditBalance: creditBalance,
            });

            let credit: PaymentCredit;
            if (createPartialCredit) {
                credit = this.uow.paymentCredits.createEntity({
                    dealerId: selectedCredit.dealerId,
                    paymentCreditTypeId: selectedCredit.paymentCreditTypeId,
                    fromPaymentId: selectedCredit.fromPaymentId,
                    creditAmount: creditBalance,
                    notes: `Created as partial credit from Id: ${selectedCredit.id}`,
                    created: new Date(),
                    createdBy: this.auth.currentUser.userName,
                }) as PaymentCredit;
            }

            this.partialCredits.push({
                lineId: minId,
                credit: createPartialCredit ? credit : null,
                selectedCredit: selectedCredit,
            });

            //updated the selected Credit
            selectedCredit.appliedToPaymentId = this.payment.id;
            selectedCredit.applied = new Date();
        }

        this.linesGrid.instance.refresh();
    }

    async addOverpaymentLine() {
        let firstNonZeroPackSlipLine = _.find(this.invoiceLines, (x) => {
            return x.parentBalance > 0;
        });
        if (!firstNonZeroPackSlipLine) firstNonZeroPackSlipLine = this.invoiceLines[0];

        let creditAmount = await this.getInput('Add Credit', 'Enter Amount');
        if (!creditAmount) return;

        this.invoiceLines.push({
            id: this.minId,
            packingSlipId: firstNonZeroPackSlipLine.packingSlipId,
            orderLine: { partNumber: `OverPayment Line` },
            packingSlip: { packingSlipNumber: firstNonZeroPackSlipLine.packingSlipNumber, invoiceNumber: firstNonZeroPackSlipLine.invoiceNumber },
            parentAmount: 0,
            paymentTotal: 0,
            parentBalance: 0,
            amount: Utility.round(creditAmount, 2),
            paymentLineTypeId: PaymentLineType.Types.OverPayment,
        });

        // this.autoTotalSet = true;
        // this.selectedMode = 'Pay Custom Amount';
        // this.payment.total = creditAmount;

        this.minId--;
        this.linesGrid.instance.refresh();
    }

    async overPaymentCheck(readyToExport?: boolean) {
        //overPayment checks
        const today = new Date();
        const overPaymentLines = _.filter(this.invoiceLines, { paymentLineTypeId: PaymentLineType.Types.OverPayment });
        if (!!overPaymentLines && overPaymentLines.length > 0) {
            //if there is a overPayment Line, then check if there are also payment lines because then there will need to be two payments created for the QB Bridge
            const linePaymentCheck = _.some(this.invoiceLines, (x) => {
                return x.paymentLineTypeId !== PaymentLineType.Types.OverPayment && x.amount > 0;
            });
            // console.log(linePaymentCheck);

            const creditAmount = _.sumBy(overPaymentLines, 'amount');

            this.overpaymentCreditPayment = this.uow.payments.createEntity({
                dealerId: this.dealer.id,
                paymentDate: today,
                total: creditAmount,
                created: today,
                createdBy: this.auth.currentUser.userName,
                paymentMethodId: this.payment.paymentMethodId,
                referenceNumber: this.payment.referenceNumber,
                notes: `OverPayment from PaymentId: ${this.payment.id}`,
                readyToExport: readyToExport ? today : null,
            }) as Payment;

            overPaymentLines.forEach((line) => {
                line.paymentId = this.overpaymentCreditPayment.id;
            });

            ////don't create payment credits for overpayments anymore
            // const overPaymentCredit = this.uow.paymentCredits.createEntity({
            //     dealerId: this.dealer.id,
            //     paymentCreditTypeId: PaymentCreditType.Types.OverPayment,
            //     fromPaymentId: this.overpaymentCreditPayment.id,
            //     // appliedToPaymentId: this.payment.id
            //     creditAmount: creditAmount,
            //     quickBooksId: 'Overpayment treated as additional payment',
            //     quickBooksExport: today,
            //     created: today,
            //     createdBy: this.auth.currentUser.userName,
            //     applied: today,
            // }) as PaymentCredit;

            //remove the amount of the overpayment from the original payment
            this.payment.total -= creditAmount;
        }
    }

    async overPaymentCreditCheck() {
        //check to see if an overpayment credit was used on the payment
        const today = new Date();
        const invoiceCreditLines = _.filter(this.invoiceLines, { paymentLineTypeId: PaymentLineType.Types.Credit });
        if (!!invoiceCreditLines && invoiceCreditLines.length > 0) {
            //check if the credit came from a payment
            for (let index = 0; index < invoiceCreditLines.length; index++) {
                const invoiceCreditLine = invoiceCreditLines[index];
                const credit = (await this.uow.paymentCredits.byId(invoiceCreditLine.creditId)) as PaymentCredit;

                //if credit came from a payment line, reset the export date to trigger the bridge to update the payment
                if (credit.fromPaymentId) {
                    const overPayment = (await this.uow.payments.byId(credit.fromPaymentId)) as Payment;
                    overPayment.quickBooksLastExport = null;
                }
            }
            await this.uow.commit();
        }
    }

    setMinLine() {
        let minId = (_.minBy(this.invoiceLines, 'id') as any).id;
        if (minId < 0) minId--;
        else minId = -1;
        this.minId = minId;
    }
    async getInput(header: string, prompt: string): Promise<number> {
        const modalRef = this.modalService.show(InputDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                dialogHeader: header,
                dialogPrompt: prompt,
                checkNumeric: false,
                checkInteger: false,
                mode: 'text',
            },
        });

        const promise = new Promise<any>(async (resolve, reject) => {
            modalRef.content.result.subscribe(async (result) => {
                if (result.result && !!result.value) {
                    resolve(result.value);
                }
                modalRef.hide();
                resolve(null);
            });
        });
        return promise;
    }
    getLineTotal(): number {
        let total = _.sumBy(this.invoiceLines, 'amount');
        return total;
    }

    async payWithPreviousOverPayment() {
        const selectedRows = this.linesGrid.instance.getSelectedRowsData();
        if (!selectedRows || selectedRows.length === 0) {
            const result = await alert(`You must select the line(s) to pay with the OverPayment`, `${this.appInfo.settings.company}'`);
            return;
        }

        console.log(selectedRows);
        console.log(this.overPayments);
        const result = await confirm(
            `Are you sure you want to pay the selected lines with a previous overpayment record?<br>Total: $${this.selectedAmount.toFixed(2)}`,
            `${this.appInfo.settings.company}'`
        );
        if (!result) return;

        const overpaymentsListObject = [];
        let i = 1;
        this.overPayments.forEach((overpayment) => {
            if (overpayment.paidTotal < overpayment.overPaymentTotal) {
                overpaymentsListObject.push({
                    id: i,
                    paymentId: overpayment.payment.id,
                    value: `PaymentId: ${overpayment.payment.id} --- Available Amount: $${Utility.round(overpayment.overPaymentTotal - overpayment.paidTotal, 2)}`,
                });
                i++;
            }
        });

        const modalRef = this.modalService.show(SelectDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                dialogHeader: 'Select',
                dialogMode: 'none',
                dialogSaveText: 'OK',
                selectMode: 'single',
                items: overpaymentsListObject,
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.success && !!result.selectedItems) {
                console.log(result.selectedItems);
                // const entities = await this.invoiceService.transferPaymentLinesToOverpayment(result.selectedItems[0].paymentId, selectedRows);
                // await this.uow.commit(entities)

                const today = new Date();
                let i = 1;
                // const paymentLines = lines;
                const updatedInvoices = [];

                //create payment lines for all the orderline details
                const entities = [];
                for (let index = 0; index < selectedRows.length; index++) {
                    const paymentLine = selectedRows[index];
                    entities.push(
                        this.uow.paymentLines.createEntity({
                            paymentId: result.selectedItems[0].paymentId,
                            orderLineDetailId: paymentLine.id > 0 ? paymentLine.id : null,
                            lineNumber: i,
                            description: null,
                            amount: paymentLine.amount,
                            created: today,
                            createdBy: this.auth.currentUser.userName,
                            paymentLineTypeId: paymentLine.paymentLineTypeId,
                            packingSlipId: paymentLine.packingSlipId,
                        }) as PaymentLine
                    );
                    i++;
                }
                await this.uow.commit(entities);

                this.close();
            }
            modalRef.hide();
        });
    }
}
